import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCustomer } from '../../context/CustomerContext';
import { Button } from '../../components/ui/button';
import { 
  User, Package, RotateCcw, Shield, MapPin, 
  Settings, LogOut, ChevronRight 
} from 'lucide-react';

const AccountLayout = ({ children }) => {
  const { customer, logout } = useCustomer();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/account', icon: User, label: 'Overview', exact: true },
    { path: '/account/orders', icon: Package, label: 'Orders' },
    { path: '/account/returns', icon: RotateCcw, label: 'Returns' },
    { path: '/account/warranty', icon: Shield, label: 'Warranty' },
    { path: '/account/addresses', icon: MapPin, label: 'Addresses' },
    { path: '/account/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-[80vh] bg-stone-50" data-testid="account-layout">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-stone-800">My Account</h1>
          <p className="text-stone-600 mt-1">
            Welcome back, {customer?.first_name || 'Customer'}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full md:w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0 transition-colors ${
                    isActive(item)
                      ? 'bg-amber-50 text-amber-800 border-l-4 border-l-amber-600'
                      : 'text-stone-600 hover:bg-stone-50 hover:text-stone-800'
                  }`}
                  data-testid={`account-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-left text-red-600 hover:bg-red-50 transition-colors"
                data-testid="account-logout-btn"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AccountLayout;
